export const Card = ({ className = '', children }) => (
  <div className={`bg-[#f9fafa] rounded-lg border border-[#dfe4ed] p-4 flex flex-col gap-2 ${className}`}>{children}</div>
)

// No vertical padding of its own — row height/spacing comes entirely from
// Card's gap, so cards stay proportional to their row count instead of
// compounding extra padding per row on top of the card-level gap.
export const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm font-normal text-[#616a79]">{label}</span>
    <span className="text-sm font-medium text-[#272d37]">{value}</span>
  </div>
)

// Bordered rect chip — used for Power mode / Battery mode.
export const ModeBadge = ({ on, onLabel = 'ON', offLabel = 'OFF' }) => (
  <span className={`inline-flex items-center justify-center w-[52px] px-2 py-0.5 rounded border text-xs font-medium ${on ? 'bg-[#e6f8e6] border-[#c0efc0] text-[#03791f]' : 'bg-[#ffeded] border-[#fedbdb] text-[#c32925]'}`}>
    {on ? onLabel : offLabel}
  </span>
)

// Dot-in-halo — used for Charge/Discharge/Precharge MOSFET rows. Label stays
// dark regardless of state; only the dot (and its halo, when on) change color.
export const StatusBadge = ({ on, onLabel = 'ON', offLabel = 'OFF' }) => (
  <span className="inline-flex items-center gap-1 text-sm font-medium text-[#272d37]">
    <span className={`flex items-center justify-center rounded-full p-[6px] ${on ? 'bg-[#c0efc0]' : ''}`}>
      <span className={`w-2 h-2 rounded-full ${on ? 'bg-[#27b83e]' : 'bg-[#97a1b4]'}`} />
    </span>
    {on ? onLabel : offLabel}
  </span>
)

const RED = '#d51d10'
const ORANGE = '#f97316'
const GREEN = '#069729'

// Upper bound (inclusive) for each color band, checked in order.
const SOC_BANDS = [
  { max: 20, color: RED },
  { max: 50, color: ORANGE },
]
const SOH_BANDS = [
  { max: 55, color: RED },
  { max: 75, color: ORANGE },
]

const bandColor = (value, bands) => bands.find((band) => value <= band.max)?.color ?? GREEN

export const ProgressStat = ({ label, value, type }) => {
  const bands = type === 'soc' ? SOC_BANDS : type === 'soh' ? SOH_BANDS : null
  // Coerced rather than type-checked: soc/soh come straight off the payload as
  // whatever JSON type the backend sent, and a numeric string has to fill the
  // bar to match the number the label is already showing.
  const numeric = value == null || value === '' ? NaN : Number(value)
  const hasValue = Number.isFinite(numeric)
  // A missing reading has to resolve to a real 0%, not an interpolated
  // "null%"/"NaN%" — that's an invalid declaration, so the browser drops the
  // width entirely and the bar (a block child) stretches to fill its track,
  // reading as a full charge for a value we don't actually have. Clamped so an
  // out-of-range reading can't run past the track either.
  const fillPct = hasValue ? Math.min(100, Math.max(0, numeric)) : 0
  const color = bands && hasValue ? bandColor(numeric, bands) : GREEN

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between items-center">
        <span className="text-sm font-normal text-[#616a79]">{label}</span>
        <span className="text-sm font-medium text-[#272d37]">{value != null ? `${value}%` : '--'}</span>
      </div>
      <div className="h-2 bg-[#dfe4ed] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-300 ease-in-out" style={{ width: `${fillPct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}
